/**
 * workflowGraphText — turning the canvas into words, branch-aware.
 *
 * Two different consumers need the graph as text, and both used to flatten it into a straight
 * line, which is now actively wrong:
 *
 *  - the AI chat that edits the canvas (`flowToText`), which cannot preserve a branch it was
 *    never told about;
 *  - the compiled-instruction fallback (`buildExecInstruction`), used when the node walker has
 *    nothing to return and for inbound comms channels — so a branching workflow meant one thing
 *    to the walker and another to the blob.
 *
 * It also answers "would replacing this graph destroy work the author did by hand?", which is
 * what stops the AI chat wiping an if/else.
 *
 * Pure and unit-tested; the components render what it returns.
 */
import type { Edge, Node } from "@xyflow/react";

import type { WfGraph, WfNodeData } from "./WorkflowCanvas";
import { conditionValue, hasRoutes, nodeAppSlugs, type WfRoute } from "./workflowRoutes";

type WfNode = Node<WfNodeData>;

/** The route an edge leaves by, if any. Routes live on the source port. */
export function edgeRoute(edge: Edge): string | undefined {
  const branch = (edge.data as { branch?: string } | undefined)?.branch;
  const handle = typeof edge.sourceHandle === "string" ? edge.sourceHandle : undefined;
  const raw = (branch ?? handle ?? "").trim();
  return raw.length > 0 ? raw : undefined;
}

/**
 * The edges that point back at an ancestor — the loops.
 *
 * "Points back" only means something once you fix where the walk starts: in a cycle
 * sheet → post → router → sheet, EVERY edge can reach its own source, so a naive test calls all
 * three a loop and the body never runs forward.
 *
 * So this is a depth-first walk that marks an edge as looping when its target is still ON the
 * stack, which is the textbook back-edge. Roots are the entry nodes first; a cycle with no entry
 * of its own is rooted at the node fed by the fewest UNROUTED edges, because a loop leaves a
 * routing step's named port while plain control flow does not. Deterministic, and it matches
 * `planGraphExecution` on the server so the canvas and the engine never disagree about which
 * edge is the loop.
 */
export function loopEdges(edges: ReadonlyArray<Edge>): ReadonlyArray<Edge> {
  const ids = [...new Set(edges.flatMap((e) => [e.source, e.target]))].sort();
  const outgoing = new Map<string, Edge[]>();
  for (const e of edges) {
    if (e.source === e.target) continue;
    const list = outgoing.get(e.source);
    if (list) list.push(e);
    else outgoing.set(e.source, [e]);
  }
  const UNSEEN = 0;
  const ON_STACK = 1;
  const SETTLED = 2;
  const mark = new Map<string, number>();
  const loops = new Set<Edge>();
  const walk = (root: string): void => {
    const stack: Array<{ readonly id: string; next: number }> = [{ id: root, next: 0 }];
    mark.set(root, ON_STACK);
    while (stack.length > 0) {
      const top = stack[stack.length - 1]!;
      const outs = outgoing.get(top.id) ?? [];
      if (top.next >= outs.length) {
        mark.set(top.id, SETTLED);
        stack.pop();
        continue;
      }
      const edge = outs[top.next]!;
      top.next += 1;
      const state = mark.get(edge.target) ?? UNSEEN;
      if (state === ON_STACK) loops.add(edge);
      else if (state === UNSEEN) {
        mark.set(edge.target, ON_STACK);
        stack.push({ id: edge.target, next: 0 });
      }
    }
  };
  const hasParent = new Set(edges.filter((e) => e.source !== e.target).map((e) => e.target));
  for (const id of ids) {
    if (!hasParent.has(id) && (mark.get(id) ?? UNSEEN) === UNSEEN) walk(id);
  }
  for (;;) {
    const unseen = ids.filter((id) => (mark.get(id) ?? UNSEEN) === UNSEEN);
    if (unseen.length === 0) break;
    const left = new Set(unseen);
    let root = unseen[0]!;
    let fewest = Number.POSITIVE_INFINITY;
    for (const id of unseen) {
      const incoming = edges.filter((e) => e.target === id && left.has(e.source));
      const plain = incoming.filter((e) => edgeRoute(e) === undefined).length;
      if (plain < fewest) {
        fewest = plain;
        root = id;
      }
    }
    walk(root);
  }
  return edges.filter((e) => loops.has(e));
}

/**
 * Does this graph contain work that a straight-line rebuild would destroy?
 *
 * True when any step routes, or any connection loops back. The AI builder can only emit a linear
 * chain, so replacing a graph like this with its output silently deletes the author's branch.
 */
export function graphHasBranching(graph: WfGraph | null | undefined): boolean {
  if (!graph) return false;
  const routed = graph.nodes.some((n) => hasRoutes((n.data as WfNodeData).routes));
  if (routed) return true;
  if (graph.edges.some((e) => edgeRoute(e) !== undefined)) return true;
  return loopEdges(graph.edges).length > 0;
}

/** A step's human name, for prose about it. */
export function stepName(node: WfNode, fallback: string): string {
  const d = node.data;
  return d.label?.trim() || nodeAppSlugs(d)[0] || fallback;
}

/** One route, described: its name, its condition, and where it goes. */
export interface RouteTarget {
  readonly route: WfRoute;
  /** Step numbers (1-based, in the given order) this route leads to. */
  readonly targets: ReadonlyArray<{ readonly step: number | null; readonly name: string }>;
  /** True when this route points back upstream. */
  readonly loops: boolean;
}

/**
 * Where each of a routing step's routes actually goes, resolved against the ordered steps so the
 * prose can say "go back to step 2" instead of naming an opaque node id.
 */
export function routeTargets(
  node: WfNode,
  graph: WfGraph,
  ordered: ReadonlyArray<WfNode>,
): ReadonlyArray<RouteTarget> {
  const routes = node.data.routes ?? [];
  const loops = new Set(loopEdges(graph.edges));
  const stepOf = new Map(ordered.map((n, i) => [n.id, i + 1]));
  const nameOf = new Map(ordered.map((n) => [n.id, stepName(n, n.data.kind)]));
  return routes.map((route) => {
    const edges = graph.edges.filter((e) => e.source === node.id && edgeRoute(e) === route.id);
    return {
      route,
      targets: edges.map((e) => ({
        step: stepOf.get(e.target) ?? null,
        name: nameOf.get(e.target) ?? e.target,
      })),
      loops: edges.some((e) => loops.has(e)),
    };
  });
}

/** How a route's condition reads in a sentence. */
export function routeConditionPhrase(route: WfRoute): string {
  const value = conditionValue(route.when);
  switch (route.when.kind) {
    case "hasMoreItems":
      return "there are still items left to process";
    case "contains":
      return `the previous result mentions "${value}"`;
    case "notContains":
      return `the previous result does NOT mention "${value}"`;
    case "ask":
      return value || "you judge it to be the case";
    case "always":
      return "nothing above matched";
  }
}

/**
 * The instruction lines for a routing step — the branch prose the compiled fallback was missing.
 *
 * Exactly one route is taken, routes are checked in order, and a looping route says to run the
 * body again with its cap, so the blob behaves like the node walker instead of guessing.
 */
export function branchInstructionLines(
  node: WfNode,
  graph: WfGraph,
  ordered: ReadonlyArray<WfNode>,
  stepNumber: string,
): ReadonlyArray<string> {
  const resolved = routeTargets(node, graph, ordered);
  if (resolved.length === 0) return [];
  const lines = [
    `${stepNumber} — IF / ELSE. Take EXACTLY ONE of the routes below, checking them in order and ` +
      `stopping at the first that applies. Then follow only that route; ignore the others.`,
  ];
  for (const { route, targets, loops } of resolved) {
    const name = route.label?.trim() || route.id;
    const where = targets.length === 0 ? "nothing is connected to it — stop here" : null;
    const dest =
      where ??
      targets
        .map((target) =>
          loops
            ? `go BACK to step ${target.step ?? "?"} (${target.name}) and run the steps from there again`
            : `continue to step ${target.step ?? "?"} (${target.name})`,
        )
        .join(", and ");
    const cap =
      loops && node.data.maxPasses
        ? ` Do this at most ${node.data.maxPasses} times in total; if you hit that limit, stop looping, take the other route, and report how much you got through.`
        : "";
    lines.push(`   • "${name}" — when ${routeConditionPhrase(route)}: ${dest}.${cap}`);
  }
  return lines;
}

/** One step, described for the AI chat that edits the canvas. */
export function describeStep(node: WfNode, graph: WfGraph, ordered: ReadonlyArray<WfNode>): string {
  const d = node.data;
  const apps = nodeAppSlugs(d);
  if (d.kind === "app") {
    const mode =
      apps.length > 1 ? ` (${d.appMode === "any" ? "any ONE of these" : "ALL of these"})` : "";
    return `App ${apps.join(" + ") || "?"}${mode}: ${(d.steps ?? []).filter(Boolean).join("; ")}`;
  }
  if (d.kind === "browser") return `Browser (your device): ${d.instruction ?? d.text ?? ""}`;
  if (d.kind === "cloudBrowser") return `Cloud Browser: ${d.instruction ?? d.text ?? ""}`;
  if (d.kind === "computer") return `Cloud Computer: ${d.instruction ?? d.text ?? ""}`;
  if (d.kind === "vault") return `Vault: ${(d.vaultIds ?? []).length} file(s) granted`;
  if (d.kind === "foreach") {
    const c = d.forEachConfig ?? {};
    const src = c.source;
    const list =
      src?.kind === "step"
        ? `the JSON list from step ${ordered.findIndex((n) => n.id === src.stepId) + 1}`
        : `Google Sheet ${src?.spreadsheetId || "?"} tab "${src?.sheet || "?"}"`;
    const skip = c.filter?.column
      ? `, skipping rows where ${c.filter.column} = "${c.filter.notEquals ?? ""}"`
      : "";
    const save = c.saveTo?.outcomeColumn
      ? `; saves each result to column "${c.saveTo.outcomeColumn}"${c.saveTo.noteColumn ? ` and note to "${c.saveTo.noteColumn}"` : ""}`
      : "";
    return (
      `For each item of ${list}${skip}: ${c.body?.kind ?? "browser"} — ${c.body?.text ?? ""} ` +
      `(${c.concurrency ?? 3} at a time, ${c.staggerSeconds ?? 20}s apart, on failure ${c.onFail ?? "skip"})${save}`
    );
  }
  if (d.kind === "decision") {
    if (!hasRoutes(d.routes)) return `Decision: continue only if — ${d.text ?? ""}`;
    // Spell out every route and where it goes, or the model rewrites the graph without them.
    const parts = routeTargets(node, graph, ordered).map(({ route, targets, loops }) => {
      const dest = targets.map((x) => `step ${x.step ?? "?"} ${x.name}`).join(" + ") || "nothing";
      return `"${route.label?.trim() || route.id}" (when ${routeConditionPhrase(route)}) → ${
        loops ? `LOOPS BACK to ${dest}` : dest
      }`;
    });
    return `If/Else — takes exactly ONE route: ${parts.join("; ")}`;
  }
  return `Output: ${d.text ?? ""}${d.channel ? ` (send on ${d.channel})` : ""}${
    d.autonomous ? " (keep replying)" : ""
  }`;
}

/**
 * The whole canvas as text for the AI chat.
 *
 * Routes and loops are stated explicitly, with a standing instruction to keep them — the model
 * replaces the entire graph with its answer, so anything left unsaid here gets deleted.
 */
export function describeFlow(
  graph: WfGraph | null | undefined,
  ordered: ReadonlyArray<WfNode>,
): string {
  const nodes = graph?.nodes ?? [];
  if (!graph || nodes.length === 0) return "(empty)";
  const lines = nodes
    .filter((n) => (n.data as WfNodeData).kind === "trigger")
    .map(
      (n) =>
        `Trigger: ${(n.data as WfNodeData).label}${
          (n.data as WfNodeData).requires?.includes("image") ? " (needs an image)" : ""
        }`,
    );
  ordered.forEach((node, i) => {
    lines.push(`${i + 1}. ${describeStep(node, graph, ordered)}`);
  });
  if (graphHasBranching(graph)) {
    lines.push(
      "",
      "This flow BRANCHES and/or LOOPS. Keep every route and every loop exactly as described " +
        "unless I explicitly ask you to change them — do not flatten it into a straight line.",
    );
  }
  return lines.join("\n");
}
